import React, { useRef, useState } from "react";
import emailjs from "emailjs-com";
import { ChevronDown, ChevronUp, CheckCircle, Briefcase } from "lucide-react";
import {
  HiAcademicCap,
  HiCalendar,
  HiMail,
  HiOfficeBuilding,
} from "react-icons/hi";
import { HiMapPin } from "react-icons/hi2";
import WhyJoinTissa from "./WhyJoinTissa";
import HeroSection from "../common/HeroSection";

const USAJobOpening = () => {
  const [openJobId, setOpenJobId] = useState(null);
  const jobRefs = useRef({});

  const toggleDetails = (jobId) => {
    setOpenJobId((prevId) => {
      const newId = prevId === jobId ? null : jobId;
      setTimeout(() => {
        if (newId !== null) {
          jobRefs.current[newId]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 300);
      return newId;
    });
  };

  const jobList = [
    {
      id: "TSDIII24",
      title: "Software Developer III",
      company: "TISSA Technology LLC",
      location: "Houston, TX",
      type: "Full-time",
      education: "Bachelor's degree or equivalent",
      description:
        "Senior developer role focused on the design and implementation of advanced software solutions for enterprise clients.",
      requirements:
        "Bachelor's +5yr/equiv.: 5 years of progressive experience in Computer Software Development.",
      jobDescription:
        "As a Software Developer III at TISSA Technology, you will be responsible for leading development initiatives, designing and implementing complex software solutions, and mentoring junior developers. You will work on critical projects for our Fortune 500 clients, leveraging your expertise in enterprise software development.",
      responsibilities: [
        "Design, develop, and implement complex software solutions based on client requirements",
        "Provide technical leadership for development teams and mentor junior developers",
        "Collaborate with project managers and business analysts to define technical requirements",
        "Review code and ensure adherence to coding standards and best practices",
        "Troubleshoot and resolve complex technical issues in production environments",
        "Stay up-to-date with emerging technologies and industry trends",
        "Participate in architecture and design discussions to identify innovative solutions",
      ],
      skills: [
        "Java",
        "Spring Boot",
        "Microservices",
        "REST API",
        "SQL",
        "NoSQL",
        "Cloud Technologies",
        "DevOps",
        "CI/CD",
      ],
      status: "Active",
    },
    {
      id: "TSDII24",
      title: "Software Developer II",
      company: "TISSA Technology LLC",
      location: "Houston, TX",
      type: "Full-time",
      education: "Bachelor's degree or equivalent",
      description:
        "Mid-level developer position with focus on Java and Spring Boot applications in Unix/Linux environments.",
      requirements:
        "Bachelor's +2yr/equiv.: 2 years of experience in Spring Boot, Java, Unix, Linux TOAD and SOAP.",
      jobDescription:
        "As a Software Developer II at TISSA Technology, you will be part of our development team responsible for building and maintaining enterprise applications. You will work closely with senior developers and project leads to implement features, fix bugs, and improve existing codebases. This role requires proficiency in Java, Spring Boot, and experience with Unix/Linux environments.",
      responsibilities: [
        "Develop and maintain software applications using Java and Spring Boot",
        "Write clean, efficient, and maintainable code following best practices",
        "Perform code reviews and implement feedback from senior developers",
        "Debug and resolve software defects in development and production environments",
        "Create and maintain technical documentation",
        "Work with Unix/Linux systems and related tools",
        "Collaborate with team members on features and improvements",
      ],
      skills: [
        "Java",
        "Spring Boot",
        "Unix",
        "Linux",
        "TOAD",
        "SOAP",
        "REST API",
        "SQL",
        "Git",
      ],
      status: "Active",
    },
    // Add more job objects as needed
  ];

  const [formErrors, setFormErrors] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    resume: null,
  });
  const [submitted, setSubmitted] = useState(false);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Full Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      errors.phone = "Phone must be 10 digits";
    }

    if (!formData.resume) {
      errors.resume = "Resume is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // Clear the error for the field being modified
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    setFormData((prev) => ({
      ...prev,
      [name]: name === "resume" ? files[0] : value,
    }));
  };

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("mobile", formData.phone);
      formDataToSend.append("position", selectedJob?.title || "N/A");
      formDataToSend.append("resume", formData.resume);
      formDataToSend.append("location", selectedJob?.location);

      const response = await fetch(
        "https://tissa-api.tissatech.in/sendmail.php",
        {
          method: "POST",
          body: formDataToSend,
        }
      );
      console.log(response);

      const result = await response.json();
      if (result.status === "ok") {
        setLoading(false);
        console.log("Mail sent:", result.message);
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setIsVisible(false);
        }, 7000);
      } else {
        alert("Failed to send application. Please try again.");
      }
    } catch (error) {
      console.error("Error sending mail:", error);
      console.log("Error sending mail:", error);
      alert("Something went wrong. Please try again.", error);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleOpenPopup = (job) => {
    setSelectedJob(job);
    setIsVisible(true);
    setFormData({
      name: "",
      email: "",
      phone: "",
      resume: null,
    });
    setFormErrors({});
  };

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 5;
  const totalPages = Math.ceil(jobList.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const currentJobs = jobList.slice(startIndex, endIndex);

  return (
    <div>
      <HeroSection
        title="Current Openings - USA"
        breadcrumb={[
          { name: "Career", url: "/career" },
          { name: "USA Careers Page" },
        ]}
        background="/assets/images/career.jpg"
      />

      {/* Popup */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
          isVisible ? "block" : "hidden"
        }`}
      >
        <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-md relative">
          <button
            onClick={handleClose}
            className="absolute top-2 right-3 text-2xl text-gray-600 hover:text-red-600"
            aria-label="Close"
          >
            &times;
          </button>

          {selectedJob && (
            <h2 className="text-xl font-semibold mb-4">
              {selectedJob.title}: {selectedJob.id}
            </h2>
          )}

          {submitted ? (
            <div className="text-green-700 bg-green-100 p-4 rounded">
              🎉 Thank you for applying! We'll contact you shortly.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                />
                {formErrors.name && (
                  <p className="text-red-500 text-xs absolute">
                    {formErrors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mt-5 font-medium">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs absolute">
                    {formErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mt-5 font-medium">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                  maxLength={10}
                />
                {formErrors.phone && (
                  <p className="text-red-500 text-xs absolute">
                    {formErrors.phone}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mt-5 font-medium">
                  Resume *
                </label>
                <input
                  type="file"
                  name="resume"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx"
                  className="w-full"
                />
                {formErrors.resume && (
                  <p className="text-red-500 text-xs absolute">
                    {formErrors.resume}
                  </p>
                )}
              </div>

              <br />

              {!loading ? (
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700"
                >
                  Submit Application
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full bg-blue-300 text-white py-2 rounded-md"
                >
                  Submiting Application...
                </button>
              )}
            </form>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto my-8 p-6 rounded-lg">
        {/* Header Section */}
        <h1 className="text-3xl font-bold text-gray-800 text-center mb-4"></h1>
        <div className="flex justify-center items-center mb-2">
          <Briefcase className="w-7 h-7 text-gray-600" />
        </div>
        <p className="text-center text-gray-600 mb-8">
          Explore our current job opportunities and take the next step in your
          career with TISSA Technology.
        </p>

        {/* Job Cards */}
        {currentJobs.map((job, index) => (
          <div
            key={job.id}
            ref={(el) => (jobRefs.current[job.id] = el)}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-lg transition-all duration-300 mb-6"
          >
            <div
              className="flex items-start justify-between p-6 cursor-pointer"
              onClick={() => toggleDetails(job.id)}
            >
              {/* Job Details */}
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {job.title}{" "}
                  <span className="text-sm text-gray-500 mx-2">
                    ID: {job.id}
                  </span>
                </h2>
                <div className="flex items-center text-sm text-gray-600 space-x-2 mt-1">
                  <span>
                    <HiOfficeBuilding className="w-4 h-4 mr-1" />
                  </span>
                  {job.company}
                  <span>
                    <HiMapPin className="w-4 h-4 mx-1" />
                  </span>
                  {job.location}
                  <span>
                    <HiCalendar className="w-4 h-4 mx-1" />
                  </span>
                  {job.type}
                  <span>
                    <HiAcademicCap className="w-4 h-4 mx-1" />
                  </span>
                  {job.education}
                </div>
                <p className="mt-2 text-gray-600">{job.description}</p>
              </div>
              {/* Status and Toggle Icon */}
              <div className="flex flex-col items-end">
                <span className="flex items-center space-x-1 text-green-900 bg-green-200 text-sm px-2 py-1 rounded-xl mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-bold">{job.status}</span>
                </span>
                {openJobId === job.id ? (
                  <ChevronUp className="w-6 h-6 text-gray-700" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-700" />
                )}
              </div>
            </div>

            {/* Expanded Section */}
            {openJobId === job.id && (
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                {/* Requirements */}
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Requirements
                </h3>
                <p className="text-gray-700 mb-4">{job.requirements}</p>

                {/* job Description */}
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Job Description
                </h3>
                <p className="text-gray-700 mb-4 text-justify">
                  {job.jobDescription}
                </p>

                {/* Responsibilities */}
                <h3 className="text-lg font-bold mb-2">Responsibilities</h3>
                <ul className="list-none space-y-2 mb-4">
                  {job.responsibilities.map((item, i) => (
                    <li key={i} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {/* Skills */}
                <h3 className="text-lg font-bold mb-2">Required Skills</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {job.skills.map((skill, i) => (
                    <span
                      key={i}
                      className="text-sm font-medium text-gray-700 bg-gray-200 px-2 py-1 rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleOpenPopup(job)}
                    className="inline-block bg-primary hover:bg-primary-dark text-black border font-medium py-2 px-6 rounded-lg transition-colors duration-300"
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Pagination Controls */}
        {jobList?.length > 5 && (
          <div className="flex justify-center items-center mt-6 space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border bg-green-300 rounded disabled:opacity-50"
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded ${
                  currentPage === i + 1 ? "bg-blue-600 text-white" : "bg-white"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 border bg-green-300 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        <div className="max-w-6xl mx-auto my-8">
          {/* Application Process Section */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Application Process
            </h2>
            <p className="text-gray-600 mb-4">
              To apply for any of the positions listed above, please mail your
              resume with the job ID # to our HR department:
            </p>
            <div className="bg-gray-100 p-4 rounded-lg text-gray-700 mb-4">
              <p className="font-bold">TISSA Technology LLC</p>
              <p>Human Resources Department</p>
              <p>2001 Timberloch Place, Suite 500</p>
              <p>The Woodlands, TX 77380</p>
            </div>
            {/* <p className="text-sm text-gray-500">
              <strong>Note:</strong> Unanticipated work site locations
              throughout U.S. Foreign equivalency accepted.
            </p> */}
          </div>

          {/* Contact Us and Location Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            {/* Contact Us */}
            <div className="bg-white shadow rounded-lg p-6 flex flex-col items-center text-center">
              <HiMail className="w-6 h-6 text-gray-600 mb-2" />
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Contact Us
              </h3>
              <p className="text-gray-600 mb-4">
                Have questions about our current openings? Feel free to reach
                out to our HR team.
              </p>
              <a
                href="mailto:careers@tissatech.com"
                className="text-primary font-medium"
              >
                careers@tissatech.com
              </a>
            </div>

            {/* Our Location */}
            <div className="bg-white shadow rounded-lg p-6 flex flex-col items-center text-center">
              <HiMapPin className="w-6 h-6 text-gray-600 mb-2" />
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Our Location
              </h3>
              <p className="text-gray-600 mb-4">
                TISSA Technology LLC <br />
                2001 Timberloch Place, Suite 500 <br />
                The Woodlands, TX 77380
              </p>
            </div>
          </div>

          <WhyJoinTissa />
        </div>
      </div>
    </div>
  );
};

export default USAJobOpening;
